import { useEffect, useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { useReserve } from "../context/ReserveContext";
import { toast } from "sonner";
import { API, fmt } from "../lib/store";

export default function ReserveModal() {
    const { product, close } = useReserve();
    const [products, setProducts] = useState([]);
    const [form, setForm] = useState({ name: "", phone: "", product_name: "", message: "" });
    const [status, setStatus] = useState("idle");

    useEffect(() => {
        if (product) {
            setForm({ name: "", phone: "", product_name: product.name, message: "" });
            setStatus("idle");
        } else {
            setForm({ name: "", phone: "", product_name: "", message: "" });
            setStatus("idle");
        }
    }, [product]);

    useEffect(() => {
        fetch(`${API}/products`).then((r) => r.json()).then(setProducts).catch(() => {});
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (status === "saving") return;
        setStatus("saving");
        try {
            const res = await fetch(`${API}/inquiries`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });
            if (!res.ok) throw new Error("failed");
            setStatus("done");
            toast.success("Request received — we'll call you shortly.");
        } catch {
            setStatus("idle");
            toast.error("Couldn't send that — give us a call instead.");
        }
    };

    return (
        <Dialog open={!!product} onOpenChange={(v) => !v && close()}>
            <DialogContent className="border-white/10 bg-ink2 text-white max-w-md" data-testid="reserve-modal">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl font-extrabold tracking-tight">
                        {status === "done" ? "You're on the list" : "Hold this for me"}
                    </DialogTitle>
                </DialogHeader>

                {status === "done" ? (
                    <div className="space-y-5 py-4">
                        <CheckCircle2 className="h-12 w-12 text-save" />
                        <p className="text-sm leading-relaxed text-slate-300">
                            We'll call or text <span className="font-bold text-white">{form.phone}</span> within
                            business hours to confirm. Your pick is held for 24 hours — no deposit needed.
                        </p>
                        <button
                            data-testid="reserve-modal-done-button"
                            onClick={close}
                            className="w-full rounded-full bg-gold py-3.5 font-mono text-xs font-bold uppercase tracking-widest text-ink transition-colors hover:bg-white"
                        >
                            Done
                        </button>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-4 py-2">
                        {product && (
                            <div className="flex items-center gap-4 rounded-xl border border-white/10 bg-ink p-3">
                                <img src={(product.images && product.images[0]) || product.image} alt="" className="h-14 w-14 rounded-lg object-cover" />
                                <div>
                                    <p className="text-sm font-bold leading-tight">{product.name}</p>
                                    <p className="font-mono text-xs text-gold">
                                        {product.our_price ? fmt(product.our_price) : "Call for price"}
                                    </p>
                                </div>
                            </div>
                        )}
                        <input
                            data-testid="contact-form-name-input"
                            required
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            placeholder="Your name"
                            className="w-full rounded-lg border border-white/15 bg-ink px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-gold focus:outline-none"
                        />
                        <input
                            data-testid="contact-form-phone-input"
                            required
                            type="tel"
                            value={form.phone}
                            onChange={(e) => setForm({ ...form, phone: e.target.value })}
                            placeholder="Phone number"
                            className="w-full rounded-lg border border-white/15 bg-ink px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-gold focus:outline-none"
                        />
                        <select
                            data-testid="contact-form-appliance-select"
                            value={form.product_name}
                            onChange={(e) => setForm({ ...form, product_name: e.target.value })}
                            className="styled-select w-full cursor-pointer rounded-lg border border-white/15 bg-ink px-4 py-3 text-sm text-white focus:border-gold focus:outline-none"
                        >
                            <option value="">Something else / not sure yet</option>
                            {products.map((p) => (
                                <option key={p.id} value={p.name}>
                                    {p.name}
                                </option>
                            ))}
                        </select>
                        <textarea
                            value={form.message}
                            onChange={(e) => setForm({ ...form, message: e.target.value })}
                            placeholder="Anything we should know? (optional)"
                            rows={3}
                            className="w-full rounded-lg border border-white/15 bg-ink px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-gold focus:outline-none resize-none"
                        />
                        <button
                            data-testid="reserve-modal-submit"
                            type="submit"
                            disabled={status === "saving"}
                            className="flex w-full items-center justify-center gap-2 rounded-full bg-gold py-3.5 font-mono text-xs font-bold uppercase tracking-widest text-ink transition-colors hover:bg-white disabled:opacity-60"
                        >
                            {status === "saving" && <Loader2 className="h-4 w-4 animate-spin" />}
                            {status === "saving" ? "Sending…" : "Hold it for 24 hours"}
                        </button>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
